// pages/LinkedAccounts/OthersLinkedAccounts.js
import React, { useState, useEffect, useCallback , useRef } from 'react';
import axios from 'axios';
import LinkIcon from '@mui/icons-material/Link'; // Used for 'Connected' status and 'Connect' button
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PropTypes from 'prop-types';
// import backendURL from '../../config';
import {
    Box, Card, CardContent, Typography, CircularProgress, Alert as MuiAlert, // Use MuiAlert
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper,
    Button, TextField, Autocomplete, Chip, IconButton, Tooltip,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import backendURL from '../../config';

// Use MuiAlert for the Alert component
const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="standard" {...props} />;
});


const getStatusChip = (status) => {
    let color = 'default';
    let label = status || 'Unknown';
    let icon = null;
    switch (status?.toLowerCase()) {
        case 'connected':
            color = 'success';
            label = 'Connected';
            icon = <LinkIcon fontSize="small" />;
            break;
        case 'disconnected':
            color = 'error';
            label = 'Disconnected';
            icon = <LinkOffIcon fontSize="small" />;
            break;
        case 'pending_approval': // Example status
            color = 'warning';
            label = 'Pending Approval';
            break;
        default:
            label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
    return <Chip label={label} color={color} size="small" icon={icon} />;
};


const OthersLinkedAccounts = ({ showMessage }) => {
    const [linkedServices, setLinkedServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmActionOpen, setConfirmActionOpen] = useState(false);
    const [serviceForAction, setServiceForAction] = useState(null);
    const [actionType, setActionType] = useState(''); // 'disconnect' or 'connect'
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [selectedUserToRequest, setSelectedUserToRequest] = useState(null);
    const [isUserSearching, setIsUserSearching] = useState(false);
    const [requestLoading, setRequestLoading] = useState(false);
    const [requestFormError, setRequestFormError] = useState('');
    const [emailSearch, setEmailSearch] = useState('');
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(null);

    const debounceTimerRef = useRef(null);
    const fetchLinkedServices = useCallback(async () => {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('my_access_token') ? localStorage.getItem('my_access_token') : localStorage.getItem('access_token');
        try {
            const response = await axios.get(`${backendURL}/linked-accounts/my-requests/sent`, { headers: { 'Authorization': `Bearer ${token}` } });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            setLinkedServices(responseData);

        } catch (err) {
            console.error("Error fetching main linked services:", err);
            const errMsg = err.response?.data?.message || "Failed to load your linked services.";
            setError(errMsg);
            if (showMessage) showMessage(errMsg, 'error');
        } finally {
            setLoading(false);
        }
    }, [showMessage]);

    useEffect(() => {
        fetchLinkedServices();
    }, [fetchLinkedServices]);

    // API call to search for users for Autocomplete
    const fetchUsersForAutocomplete = useCallback(async (searchTerm) => {
        if (searchTerm.trim().length < 2) { // Minimum characters to trigger search
            setSearchedUsers([]);
            setIsUserSearching(false);
            return;
        }
        setIsUserSearching(true);
        const token = localStorage.getItem('my_access_token') ? localStorage.getItem('my_access_token') : localStorage.getItem('access_token');
        try {
            // Replace with your actual API endpoint for user search
            // Assuming it expects a 'query' parameter
            const response = await axios.get(`${backendURL}/linked-accounts/my-requests/search-owners`, {
                params: { query: searchTerm },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            setSearchedUsers(responseData);
        } catch (err) {
            console.error("Error searching users:", err);
            setSearchedUsers([]); // Clear results on error
            // Optionally, show a message to the user via showMessage or a specific error state for search
            if (showMessage) showMessage("Failed to search for accounts. Please try again.", 'warning');
        } finally {
            setIsUserSearching(false);
        }
    }, [showMessage]); // backendURL is stable from import

    // Debounced version of fetchUsersForAutocomplete
    const debouncedFetchUsers = useCallback((searchTerm) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            fetchUsersForAutocomplete(searchTerm);
        }, 500); // 500ms debounce delay
    }, [fetchUsersForAutocomplete]);

    // Effect to trigger debounced search when emailSearch input changes
    useEffect(() => {
        if (emailSearch && emailSearch.trim().length > 0) {
            debouncedFetchUsers(emailSearch);
        } else {
            setSearchedUsers([]); // Clear results if search input is empty
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current); // Clear pending timeout if input is cleared
            }
        }
        // Cleanup timeout on component unmount or when emailSearch/debouncedFetchUsers changes
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [emailSearch, debouncedFetchUsers]);

    const handleOpenConfirmDialog = (service, type) => {
        setServiceForAction(service);
        setActionType(type);
        setConfirmActionOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmActionOpen(false);
        setServiceForAction(null);
        setActionType('');
    };

    
    const handleSendRequest = async () => {
        if (!selectedUserToRequest) {
            setRequestFormError('Please select a account to request.');
            return;
        }
        setRequestLoading(true);
        setRequestFormError('');
        const token = localStorage.getItem('my_access_token') ? localStorage.getItem('my_access_token') : localStorage.getItem('access_token');
        try {
            // --- MOCK API CALL ---
            await axios.post(`${backendURL}//linked-accounts/my-requests/initiate`, { ownerUserIdToRequest: selectedUserToRequest.user_id }, { headers: { 'Authorization': `Bearer ${token}` } });
            // Update message to use correct fields
            if (showMessage) showMessage(`Access Request added for  ${selectedUserToRequest.user_email || (selectedUserToRequest.chatbot_name || 'the selected account')}.`, 'success');
            setSelectedUserToRequest(null);
            setEmailSearch('');
            setSearchedUsers([]);
            fetchLinkedServices();
        } catch (error) {
            console.error('Error access request:', error);
            const errMsg = error.response?.data?.message || 'Failed to add access request.';
            setRequestFormError(errMsg);
            if (showMessage) showMessage(errMsg, 'error');
        } finally {
            setRequestLoading(false);
        }
    };


    const handleConfirmAction = async () => {
        if (!serviceForAction || !actionType) return;

        const token = localStorage.getItem('my_access_token') ? localStorage.getItem('my_access_token') : localStorage.getItem('access_token');

        try {

            if (actionType === 'DISCONNECTED') {
                // --- MOCK DISCONNECT ---
                // Replace with actual API call:
                await axios.post(`${backendURL}/linked-accounts/my-requests/${serviceForAction.id}/disconnect`, {}, { headers: { 'Authorization': `Bearer ${token}` } });
                
                
                const my_access_token = localStorage.getItem('my_access_token');
                const my_user = localStorage.getItem('my_user');
                localStorage.setItem('access_token', my_access_token);
                localStorage.setItem('user', my_user);
                
                localStorage.removeItem('my_access_token');
                localStorage.removeItem('my_user');
                window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: JSON.parse(my_user) }));
                if (showMessage) showMessage(`${serviceForAction.email} access has been disconnected successfully.`, 'success');
                // --- END MOCK DISCONNECT ---
            } else if (actionType === 'CONNECTED') {
                let oldConnection = linkedServices.find((item)=> item.status == 'CONNECTED')
                if (oldConnection) {
                    let errMsg = `${oldConnection.email} is already connected , please disconnect this first before connect new connection.`
                    if (showMessage) showMessage(errMsg, 'error');
                    return
                }

                // --- MOCK CONNECT ---
                // Replace with actual API call:
                const response = await axios.post(`${backendURL}/linked-accounts/my-requests/${serviceForAction.id}/connect`, {}, { headers: { 'Authorization': `Bearer ${token}` } });
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

                const access_token = localStorage.getItem('access_token');
                const user = localStorage.getItem('user');
                localStorage.setItem('my_access_token', access_token);
                localStorage.setItem('my_user', user);
                
                // Store Token
                localStorage.setItem('access_token', responseData.access_token);
                localStorage.setItem('user', JSON.stringify(responseData.user));
                
                window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: responseData.user  }));
                if (showMessage) showMessage(`${serviceForAction.email} access has been connected successfully.`, 'success'); // Adjust message as needed
                // --- END MOCK CONNECT ---
            }

            fetchLinkedServices(); // Refresh the list
        } catch (err) {
            console.error(`Error ${actionType}ing service:`, err);
            const errMsg = err.response?.data?.message || `Failed to ${actionType} ${serviceForAction.name}.`;
            if (showMessage) showMessage(errMsg, 'error');
        } finally {
            handleCloseConfirmDialog();
        }
    };

    if (loading && linkedServices.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, minHeight: 200 }}><CircularProgress /></Box>;
    }

    if (error && linkedServices.length === 0 && !loading) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }

    const getDialogTitle = () => {
        if (actionType === 'DISCONNECTED') return "Confirm Disconnection";
        if (actionType === 'CONNECTED') return "Confirm Connection";
        return "Confirm Action";
    };

    const getDialogContentText = () => {
        console.log(serviceForAction)
        if (actionType === 'DISCONNECTED') {
            return `Are you sure you want to disconnect "${serviceForAction?.email}" account?.`;
        }
        if (actionType === 'CONNECTED') {
            return `Are you sure you want to connect for "${serviceForAction?.email}" account?.`;
        }
        return "";
    };

    const getDialogButtonText = () => {
        if (actionType === 'DISCONNECTED') return "Disconnect";
        if (actionType === 'CONNECTED') return "Connect";
        return "Confirm";
    }


    return (

         <Box>
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" component="div" gutterBottom sx={{ mb: 2 }}>
                        Request Others Accounts to Use
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-start"> {/* Changed to flex-start */}
                        <Grid item xs={12} md={7} lg={8}>
                            <Autocomplete
                                id="user-search-autocomplete"
                                options={searchedUsers} // This should now be a proper array
                                getOptionLabel={(option) => {
                                    // MUI calls this with the option object, or sometimes a string (e.g. current input value)
                                    if (typeof option === 'string') {
                                        return option;
                                    }
                                    if (!option || !option.user_email) { // Guard against null/undefined or incomplete option
                                        return '';
                                    }
                                    // Format: "user_email (Chatbot: chatbot_name)" or "user_email (Website: website_address)" or just "user_email"
                                    const email = option.user_email;
                                    let detail = '';
                                    if (option.chatbot_name) {
                                        detail = `Chatbot: ${option.chatbot_name}`;
                                    } else if (option.website_address) {
                                        detail = `Website: ${option.website_address}`;
                                    }
                                    return detail ? `${email} (${detail})` : email;
                                }}
                                isOptionEqualToValue={(option, value) => option && value && option.user_id === value.user_id} // Use user_id
                                inputValue={emailSearch}
                                onInputChange={(event, newInputValue) => setEmailSearch(newInputValue)}
                                onChange={(event, newValue) => setSelectedUserToRequest(newValue)}
                                loading={isUserSearching}
                                value={selectedUserToRequest}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search by email, chatbot name, or website address" // Updated label
                                        variant="outlined"
                                        placeholder="Start typing to find accounts..."
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isUserSearching ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    // props already includes key, but ensure option.user_id is unique and stable
                                    <li {...props} key={option.user_id}> 
                                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <Typography variant="subtitle1" component="div">
                                                {option.user_email}
                                            </Typography>
                                            {/* Conditionally render chatbot_name if it exists */}
                                            {option.chatbot_name && (
                                                <Typography variant="body2" color="text.secondary" component="div">
                                                    Chatbot: {option.chatbot_name}
                                                </Typography>
                                            )}
                                            {/* Conditionally render website_address if it exists and no chatbot_name */}
                                            {option.website_address && (
                                                <Typography variant="body2" color="text.secondary" component="div">
                                                    Website: {option.website_address}
                                                </Typography>
                                            )}
                                        </Box>
                                    </li>
                                )}
                                noOptionsText={emailSearch.trim().length < 2 ? "Type at least 2 characters" : (isUserSearching ? "Searching..." : "No users found")}
                            />
                        </Grid>
                        <Grid item xs={12} md={5} lg={4} sx={{pt: {xs:1, md: '16px !important'}}}> {/* Align button better */}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSendRequest}
                                disabled={!selectedUserToRequest || requestLoading || isUserSearching}
                                startIcon={requestLoading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                                fullWidth
                                sx={{ height: '56px' }} // Match TextField height
                            >
                                {requestLoading ? 'Adding Access Request...' : 'Add Access Request'}
                            </Button>
                        </Grid>
                    </Grid>
                    {requestFormError && <Alert severity="error" sx={{ mt: 2 }}>{requestFormError}</Alert>}
                </CardContent>
            </Card>
        <Card variant="outlined" sx={{ width: '100%' }}>
            <CardContent>
                <Typography variant="h6" component="div" gutterBottom sx={{ mb: 2 }}>
                   Other Accounts linked with me {/* Changed title for clarity */}
                </Typography>
                {error && linkedServices.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{error} (Displaying cached or older data)</Alert>}

                {linkedServices.length === 0 && !loading && !error ? (
                    <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        You haven't linked any of accounts yet.
                    </Typography>
                ) : linkedServices.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 600 }} aria-label="main linked accounts table">
                            <TableHead>
                                <TableRow sx={{ '& th': { fontWeight: '600', backgroundColor: 'action.hover' } }}>
                                    <TableCell>Chatbot/Website Name<br /><small>Email</small></TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Request Initiated at</TableCell>
                                    <TableCell>Last Connected at</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {linkedServices.map((item) => (
                                    <TableRow hover key={item.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>
                                            <Typography variant="body1" component="div">{item.chatbot_name ? item.chatbot_name : (item.website_name ? item.website_name : 'N/A')}</Typography>
                                            <Typography variant="caption" color="textSecondary">{item.email}</Typography>
                                        </TableCell>
                                        <TableCell>{getStatusChip(item.status)}</TableCell>
                                        <TableCell>{item.request_initiated_at || 'N/A'}</TableCell>
                                        <TableCell>{item.last_connected_at || 'N/A'}</TableCell>
                                        <TableCell align="center">
                                            {item.status === 'CONNECTED' && (
                                                <Tooltip title="Disconnect this account" arrow>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleOpenConfirmDialog(item, 'DISCONNECTED')}
                                                        startIcon={<LinkOffIcon />}
                                                    >
                                                        Disconnect
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {item.status === 'DISCONNECTED' && (
                                                <Tooltip title="Connect this account" arrow>
                                                    <Button
                                                        variant="outlined"
                                                        color="success" // Or "primary"
                                                        size="small"
                                                        onClick={() => handleOpenConfirmDialog(item, 'CONNECTED')}
                                                        startIcon={<LinkIcon />} // Using LinkIcon for connect
                                                    >
                                                        Connect
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {(item.status === 'PENDING') && (
                                                <Typography variant="caption" color="textSecondary">No actions available</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : null}
            </CardContent>

            <Dialog
                open={confirmActionOpen}
                onClose={handleCloseConfirmDialog}
                aria-labelledby="confirm-action-dialog-title"
            >
                <DialogTitle id="confirm-action-dialog-title">{getDialogTitle()}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {getDialogContentText()}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
                    <Button
                        onClick={handleConfirmAction}
                        color={actionType === 'DISCONNECTED' ? "error" : "primary"} // 'connect' can be primary or success
                        variant="contained"
                        autoFocus
                    >
                        {getDialogButtonText()}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
        </Box>
    );
};

OthersLinkedAccounts.propTypes = {
    showMessage: PropTypes.func,
};

export default OthersLinkedAccounts;